// The shared report screen. A page supplies its records and a description of
// its columns, filters, tabs and KPIs; everything below - the command bar,
// filter card, KPI row, trend, grid and drawer - is the same on every report.
//
// The page keeps its own data fetching and its own filter logic: `filters`
// and `searchMatch` are plain predicates over the records it hands in.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  BarChart2,
  ChevronUp,
  Columns,
  Download,
  Filter,
  Printer,
  RotateCcw,
  Search,
  X,
} from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { setheadertoggle } from "../../redux/toggle";
import {
  exportExcel as defaultExcel,
  exportPDF as defaultPDF,
  printReport as defaultPrint,
} from "../../core/utils/reportUtils";
import { Caret, FilterChips, Kpi, MultiSelect, Toast } from "./ReportParts";
import ReportTable, { isNumCol } from "./ReportTable";
import TrendChart, { chartWindowFor } from "./TrendChart";
import ReportDrawer from "./ReportDrawer";
import {
  PRESETS,
  daysBetween,
  dmy,
  intg,
  iso,
  money,
  monthLabel,
  pct2,
  presetRange,
} from "./reportFormat";

const ReportScreen = ({
  title,
  subtitle = "",
  rows = [],
  dateKey = "date",
  tabs = [],
  filters = [],
  kpis = [],
  chart = {},
  searchMatch,
  searchPlaceholder = "Search…",
  exportName,
  reportNames = {},
  exporters = {},
  onRefresh,
  drawerContent,
  // Reports open on today: the grid, tabs and KPIs then work over a day's
  // records instead of the whole history. Reset returns here too.
  defaultPreset = "today",
  defaultTab,
  toolbarExtras,
  unit = "records",
  // (from, to) as YYYY-MM-DD, "" for open; for pages that load by period
  onPeriodChange,
}) => {
  const dispatch = useDispatch();
  const { togglehead } = useSelector((state) => state.headerToggle);
  const handletogglehead = () => dispatch(setheadertoggle(!togglehead));
  const renderTooltip = (msg) => <Tooltip id="axr-tip">{msg}</Tooltip>;

  /* ---------------- state ---------------- */
  const initial = presetRange(defaultPreset) || ["", ""];
  const [preset, setPreset] = useState(defaultPreset);
  const [fromDate, setFromDate] = useState(initial[0]);
  const [toDate, setToDate] = useState(initial[1]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [values, setValues] = useState(() =>
    Object.fromEntries(filters.map((f) => [f.key, []]))
  );
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);
  const [sort, setSort] = useState(() =>
    Object.fromEntries(tabs.map((t) => [t.id, t.defaultSort || { k: dateKey, d: -1 }]))
  );
  const [hiddenCols, setHiddenCols] = useState(() =>
    Object.fromEntries(tabs.map((t) => [t.id, (t.columns || []).filter((c) => c.off).map((c) => c.k)]))
  );
  const [groupBy, setGroupBy] = useState("");
  const [dense, setDense] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showChart, setShowChart] = useState(true);
  const [chartRange, setChartRange] = useState("30d");
  const [bucket, setBucket] = useState("auto");
  const [openPop, setOpenPop] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const searchRef = useRef(null);
  const toastTimer = useRef(null);

  const periodHandler = useRef(onPeriodChange);
  periodHandler.current = onPeriodChange;
  useEffect(() => {
    periodHandler.current?.(fromDate, toDate);
  }, [fromDate, toDate]);

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 2600);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput), 140);
    return () => clearTimeout(t);
  }, [searchInput]);

  const tab = tabs.find((t) => t.id === activeTab) || tabs[0];

  /* ---------------- filtering ---------------- */
  const q = searchText.trim().toLowerCase();

  const matches = useCallback(
    (r, from, to) => {
      const d = r[dateKey];
      if (from && (!d || d < from)) return false;
      if (to && (!d || d > to)) return false;
      for (const f of filters) {
        const v = values[f.key] || [];
        if (v.length && !f.match(r, v)) return false;
      }
      if (q && searchMatch && !searchMatch(r, q)) return false;
      return true;
    },
    [dateKey, filters, values, q, searchMatch]
  );

  const filtered = useMemo(
    () => rows.filter((r) => matches(r, fromDate, toDate)),
    [rows, matches, fromDate, toDate]
  );

  // the same window immediately before - drives the KPI deltas
  const prevRows = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const len = daysBetween(fromDate, toDate) + 1;
    const pTo = iso(dayjs(fromDate).subtract(1, "day"));
    const pFrom = iso(dayjs(pTo).subtract(len - 1, "day"));
    return rows.filter((r) => matches(r, pFrom, pTo));
  }, [rows, matches, fromDate, toDate]);

  /* ---------------- per-tab rows ---------------- */
  const tabRows = useMemo(() => {
    const out = {};
    tabs.forEach((t) => {
      out[t.id] = t.rows ? t.rows(filtered) : filtered;
    });
    return out;
  }, [tabs, filtered]);

  const columns = (tab?.columns || []).filter((c) => !(hiddenCols[tab?.id] || []).includes(c.k));
  const groupOptions = tab?.groupOptions || [];
  const grouping = !!groupBy && groupOptions.length > 0;

  const groupOf = useCallback(
    (r) => {
      if (!groupBy) return "";
      if (groupBy === "__month") return monthLabel(String(r[dateKey] || "").slice(0, 7));
      const v = r[groupBy];
      return v === undefined || v === null || v === "" ? "—" : String(v);
    },
    [groupBy, dateKey]
  );

  const list = useMemo(() => {
    const base = (tabRows[tab?.id] || []).map((r) => ({ ...r }));
    const s = sort[tab?.id] || { k: dateKey, d: -1 };
    base.sort((a, b) => {
      const av = a[s.k];
      const bv = b[s.k];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * s.d;
      return String(av ?? "").localeCompare(String(bv ?? "")) * s.d;
    });
    if (grouping) base.sort((a, b) => String(groupOf(a)).localeCompare(String(groupOf(b))));
    base.forEach((r, i) => (r.__sl = i + 1));
    return base;
  }, [tabRows, tab, sort, grouping, groupOf, dateKey]);

  /* ---------------- KPIs ---------------- */
  const kpiTiles = kpis.map((k) => {
    const now = k.value(filtered);
    const before = prevRows ? k.value(prevRows) : null;
    const delta =
      before === null || !before ? null : ((now - before) / Math.abs(before)) * 100;
    return {
      label: k.label,
      value: k.format === "int" ? intg(now) : money(now),
      delta,
      invert: k.invert,
      hero: k.hero,
    };
  });

  /* ---------------- trend ---------------- */
  const chartOn = chart !== false;
  const chartValueKey = chart.valueKey || "net";
  const allDates = useMemo(() => rows.map((r) => r[dateKey]), [rows, dateKey]);
  const win = chartWindowFor(chartRange, fromDate, toDate, allDates);
  const chartRows = useMemo(
    () => (chartOn ? rows.filter((r) => matches(r, win[0], win[1])) : []),
    [chartOn, rows, matches, win[0], win[1]]
  );

  /* ---------------- filter chips ---------------- */
  const chips = [];
  if (searchText)
    chips.push({
      label: "Search",
      value: searchText,
      clear: () => {
        setSearchInput("");
        setSearchText("");
      },
    });
  filters.forEach((f) => {
    const v = values[f.key] || [];
    if (!v.length) return;
    chips.push({
      label: f.label,
      value: v.length === 1 ? f.items.find((i) => i.v === v[0])?.t || "1 selected" : `${v.length} selected`,
      clear: () => setValues((s) => ({ ...s, [f.key]: [] })),
    });
  });

  /* ---------------- handlers ---------------- */
  const applyPreset = (id) => {
    setPreset(id);
    const r = presetRange(id);
    if (r) {
      setFromDate(r[0]);
      setToDate(r[1]);
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchText("");
    setValues(Object.fromEntries(filters.map((f) => [f.key, []])));
    setGroupBy("");
    applyPreset(defaultPreset);
  };

  const onSort = (c) => {
    const k = c.sortKey || c.k;
    setSort((s) => {
      const cur = s[tab.id] || {};
      return {
        ...s,
        [tab.id]: cur.k === k ? { k, d: -cur.d } : { k, d: isNumCol(c) ? -1 : 1 },
      };
    });
  };

  const openDrawer = (row) => {
    if (!drawerContent) return;
    setDrawerRow(row);
    requestAnimationFrame(() => requestAnimationFrame(() => setDrawerOpen(true)));
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerRow(null), 200);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest(".axr-ms") && !e.target.closest(".axr-popwrap")) setOpenPop(null);
    };
    const onKey = (e) => {
      if (e.key === "F3") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if (e.key === "Escape") {
        if (drawerOpen) closeDrawer();
        else setOpenPop(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  /* ---------------- export ---------------- */
  // the same shape the existing report utils expect: title -> key mapping
  const getActiveTabData = () => {
    const cols = columns
      .filter((c) => c.type !== "share")
      .map((c) => ({ title: c.t, dataIndex: c.exportKey || c.k }));
    return {
      data: list,
      columns: cols,
      Rname: reportNames[tab?.id] || exportName || title,
      fieldMapping: Object.fromEntries(cols.map((c) => [c.title, c.dataIndex])),
    };
  };

  const doExcel = exporters.excel || defaultExcel;
  const doPDF = exporters.pdf || defaultPDF;
  const doPrint = exporters.print || defaultPrint;

  const downloadCSV = () => {
    const cols = columns.filter((c) => c.type !== "share" && c.type !== "index");
    const cell = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.map((c) => cell(c.t)).join(",")]
      .concat(
        list.map((r) =>
          cols.map((c) => cell(isNumCol(c) ? pct2(r[c.exportKey || c.k]) : r[c.exportKey || c.k])).join(",")
        )
      )
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${reportNames[tab?.id] || exportName || title}${fromDate ? `_${fromDate}_${toDate}` : ""}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    toast(`CSV downloaded · ${list.length} rows`);
  };

  const rangeText = fromDate && toDate ? `${dmy(fromDate)} → ${dmy(toDate)}` : "All dates";

  return (
    <div className={"axr-screen" + (dense ? " axr-dense" : "")}>
      <div className="page-wrapper axr-wrapper">
        <div className="content axr-page">
          {/* ============ REPORT HEADER ============ */}
          <div className="axr-rhead">
            <div className="axr-rtitle">
              <h2>
                {title}
                <span className="axr-range">{rangeText}</span>
              </h2>
              <p>
                {subtitle}
                {subtitle ? " · " : ""}
                <b>{intg(filtered.length)}</b> {unit}
              </p>
            </div>
            <div className="axr-rtools">
              {toolbarExtras}
              <div className="axr-popwrap">
                <button
                  type="button"
                  className="axr-iconbtn"
                  aria-expanded={openPop === "cols"}
                  onClick={() => setOpenPop(openPop === "cols" ? null : "cols")}
                  title="Choose columns"
                >
                  <Columns />
                  Columns
                </button>
                {openPop === "cols" && (
                  <div className="axr-pop right">
                    <div className="axr-pop-h">Columns on this tab</div>
                    {(tab?.columns || []).map((c) => (
                      <label className="axr-pop-opt" key={c.k}>
                        <input
                          type="checkbox"
                          checked={!(hiddenCols[tab.id] || []).includes(c.k)}
                          onChange={(e) =>
                            setHiddenCols((h) => ({
                              ...h,
                              [tab.id]: e.target.checked
                                ? (h[tab.id] || []).filter((x) => x !== c.k)
                                : [...(h[tab.id] || []), c.k],
                            }))
                          }
                        />
                        {c.t}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {chartOn && (
                <button
                  type="button"
                  className="axr-iconbtn"
                  aria-pressed={showChart}
                  onClick={() => setShowChart((v) => !v)}
                  title="Show or hide the trend"
                >
                  <BarChart2 />
                  Trend
                </button>
              )}
              <button
                type="button"
                className="axr-iconbtn"
                aria-pressed={showFilters}
                onClick={() => setShowFilters((v) => !v)}
                title="Show or hide filters"
              >
                <Filter />
                Filters
              </button>
              <span className="axr-sepbar" />
              {onRefresh && (
                <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                  <button
                    type="button"
                    className="axr-iconbtn"
                    aria-label="Refresh"
                    onClick={() => {
                      onRefresh();
                      toast("Data reloaded");
                    }}
                  >
                    <RotateCcw />
                  </button>
                </OverlayTrigger>
              )}
              <button type="button" className="axr-iconbtn" onClick={downloadCSV} title="Download CSV">
                <Download />
                CSV
              </button>
              <OverlayTrigger placement="top" overlay={renderTooltip("Excel")}>
                <Link to="#" className="axr-iconbtn" onClick={() => doExcel(getActiveTabData, exportName)}>
                  <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  Excel
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("PDF")}>
                <Link to="#" className="axr-iconbtn" onClick={() => doPDF(getActiveTabData, exportName)}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  PDF
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Print")}>
                <Link
                  to="#"
                  className="axr-iconbtn"
                  aria-label="Print"
                  onClick={() => doPrint(getActiveTabData, exportName)}
                >
                  <Printer />
                </Link>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                <Link
                  to="#"
                  aria-label="Collapse"
                  id="collapse-header"
                  className={"axr-iconbtn" + (togglehead ? " active" : "")}
                  onClick={handletogglehead}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </div>
          </div>

          {/* ============ FILTERS ============ */}
          {showFilters && (
            <section className="axr-card axr-filters">
              <div className="axr-frow top">
                <div className="axr-fld axr-searchfld">
                  <label htmlFor="axr-q">Search</label>
                  <Search className="axr-sicon" />
                  <input
                    id="axr-q"
                    ref={searchRef}
                    autoComplete="off"
                    placeholder={searchPlaceholder}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && setSearchText(searchInput)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="axr-clr"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchInput("");
                        setSearchText("");
                        searchRef.current?.focus();
                      }}
                    >
                      <X />
                    </button>
                  )}
                  <span className="axr-kbd">F3</span>
                </div>
                <div className="axr-fld sel">
                  <label htmlFor="axr-preset">Period</label>
                  <select
                    id="axr-preset"
                    value={preset}
                    onChange={(e) =>
                      e.target.value === "custom" ? setPreset("custom") : applyPreset(e.target.value)
                    }
                  >
                    {PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <Caret />
                </div>
                <div className="axr-fld">
                  <label htmlFor="axr-from">From date</label>
                  <input
                    id="axr-from"
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      if (toDate && e.target.value && e.target.value > toDate) setToDate(e.target.value);
                      setPreset("custom");
                    }}
                  />
                </div>
                <div className="axr-fld">
                  <label htmlFor="axr-to">To date</label>
                  <input
                    id="axr-to"
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPreset("custom");
                    }}
                  />
                </div>
                <div className="axr-fbtns">
                  <button
                    type="button"
                    className="axr-btn axr-btn-primary"
                    onClick={() => {
                      setSearchText(searchInput);
                      toast("Report refreshed");
                    }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="axr-btn axr-btn-ghost"
                    onClick={() => {
                      resetFilters();
                      toast("Filters cleared");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="axr-presets">
                {PRESETS.filter((p) => p.id !== "custom").map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="axr-pchip"
                    aria-pressed={preset === p.id}
                    onClick={() => applyPreset(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {filters.length > 0 && (
                <div className="axr-frow dims">
                  {filters.map((f) => (
                    <MultiSelect
                      key={f.key}
                      id={f.key}
                      label={f.label}
                      items={f.items}
                      value={values[f.key] || []}
                      onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                      openPop={openPop}
                      setOpenPop={setOpenPop}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          <FilterChips chips={chips} onClearAll={resetFilters} />

          {/* ============ KPI ============ */}
          {kpiTiles.length > 0 && (
            <div className="axr-kpis">
              {kpiTiles.map((k) => (
                <Kpi key={k.label} {...k} />
              ))}
            </div>
          )}

          {/* ============ TREND ============ */}
          {chartOn && showChart && (
            <TrendChart
              title={chart.title || "Net total over time"}
              rows={chartRows}
              dateKey={dateKey}
              valueKey={chartValueKey}
              win={win}
              bucket={bucket}
              onBucketChange={setBucket}
              chartRange={chartRange}
              onRangeChange={setChartRange}
            />
          )}

          {/* ============ TABLE ============ */}
          <ReportTable
            tabs={tabs.map((t) => ({ id: t.id, label: t.label, count: (tabRows[t.id] || []).length }))}
            activeTab={tab?.id}
            onTabChange={(id) => {
              setActiveTab(id);
              setOpenPop(null);
            }}
            columns={columns}
            list={list}
            sort={sort[tab?.id] || { k: dateKey, d: -1 }}
            onSort={onSort}
            groupOptions={groupOptions}
            groupBy={groupBy}
            onGroupChange={setGroupBy}
            groupOf={grouping ? groupOf : null}
            dense={dense}
            onDenseToggle={() => setDense((v) => !v)}
            rowKey={tab?.rowKey}
            selectedKey={drawerRow && tab?.rowKey ? tab.rowKey(drawerRow) : undefined}
            onRowClick={drawerContent && tab?.drillDown !== false ? openDrawer : undefined}
            shareKey={tab?.shareKey || chartValueKey}
            emptyTitle={`No ${unit} in this selection`}
          />

          <footer className="axr-status">
            <span className="axr-sg">
              <span className="axr-kbd">F3</span>Search
            </span>
            <span className="axr-sg">
              <span className="axr-kbd">Enter</span>Apply
            </span>
            <span className="axr-sg">
              <span className="axr-kbd">Esc</span>Close panel
            </span>
            <span className="axr-sp" />
            <span className="axr-sg">
              {intg(filtered.length)} of {intg(rows.length)} {unit}
            </span>
          </footer>
        </div>
      </div>

      <ReportDrawer
        content={drawerRow && drawerContent ? drawerContent(drawerRow) : null}
        open={drawerOpen}
        onClose={closeDrawer}
      />
      <Toast message={toastMsg} />
    </div>
  );
};

export default ReportScreen;
